import React, { useState } from 'react'
import { Link, graphql, useStaticQuery } from 'gatsby'
import { Flex, Box, Text, Button, Image } from 'rebass/styled-components'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import { get } from 'lodash'

import { getDashboardCreateUserURL, getDashboardLoginUserURL } from '../utils/cta-utils'

import Burger from './Burger'
import theme from '../lib/theme'

const Header = () => {
  const [open, setOpen] = useState(false)

  const { t } = useTranslation('header')
  const links = t('links', { returnObjects: true })
  const data = useStaticQuery(graphql`
    query {
      logo: file(relativePath: { eq: "header/logo.svg" }) {
        publicURL
      }
    }
  `)

  const logo = get(data, 'logo.publicURL')

  return (
    <Flex
      as="header"
      id="home"
      bg="white"
      width={1}
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 999
      }}
    >
      <Flex
        justifyContent={['space-between', 'space-between', 'flex-start']}
        alignItems="center"
        maxWidth="container"
        width={1}
        mx="auto"
        pt={[10, 10, 7]}
        px={[5, 5, 0]}
        pb={5}
      >
        <Link to={'/'}>
          <Image
            src={logo}
            alt="Logo Arena"
            height="28px"
            width="112px"
            sx={{
              transition: 'all .2s ease-in-out',

              '&:hover': {
                opacity: 0.6
              }
            }}
          />
        </Link>

        <Burger open={open} onClick={() => setOpen(!open)} />

        <Flex
          flexDirection={['column', 'column', 'row']}
          alignItems="center"
          justifyContent="space-between"
          width={1}
          sx={{
            '@media screen and (max-width: 80rem)': {
              position: 'fixed',
              transition: 'all .325s ease-in-out',
              zIndex: '999',
              height: 'calc(100vh - 80px)',
              transform: 'translateY(130%)',
              top: '80px',
              left: '0',
              bg: 'white',
              justifyContent: 'center',

              '&': open && {
                transform: 'translateY(0)'
              }
            }
          }}
        >
          <Flex
            as="nav"
            ml={[0, 0, 20]}
            flexDirection={['column', 'column', 'row']}
            alignItems="center"
          >
            {Object.entries(links).map(([key, value]) => (
              <Link
                key={key}
                to={key === 'solutions' ? `/${key}/sports` : `/${key}`}
                css={{
                  '&:hover > p': value.open_links && {
                    paddingTop: '8px',
                    paddingBottom: '8px'
                  },
                  '&:hover > div': value.open_links && {
                    display: 'inline-block',
                    animation: '0.365s bottomToUp',

                    '@keyframes bottomToUp': {
                      from: {
                        transform: 'translateY(24px)'
                      },
                      to: {
                        transform: 'translateY(0)'
                      }
                    }
                  }
                }}
              >
                <Text as="p" variant="link" padding={1} mr={[0, 0, 9]} mb={[3, 3, 0]}>
                  {value.title}
                </Text>

                {value.open_links && (
                  <Box
                    display="none"
                    bg="white"
                    py={8}
                    px={6}
                    sx={{
                      position: 'absolute',
                      borderRadius: '10px',
                      boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.14), 0px 3px 4px rgba(0, 0, 0, 0.12)'
                    }}
                  >
                    {Object.entries(value.sub_links).map(([item, index]) => (
                      <Link key={item} to={`/${key}/${item}`}>
                        <Text as="p" py={2} variant="link">
                          {index}
                        </Text>
                      </Link>
                    ))}
                  </Box>
                )}
              </Link>
            ))}
          </Flex>

          <Flex
            justifyContent={['flex-start', 'flex-start', 'flex-end']}
            alignItems="center"
            mt={[3, 3, 0]}
            flexDirection={['column', 'column', 'row']}
          >
            <Link to={getDashboardLoginUserURL()}>
              <Text variant="link" color="purple" fontWeight={500} letterSpacing={0}>
                {t('log_in')}
              </Text>
            </Link>

            <Box ml={[0, 12, 12]} mt={[5, 5, 0]} mx={['auto', 0]}>
              <Link
                to={getDashboardCreateUserURL()}
                css={{
                  '&:hover': {
                    opacity: 1
                  }
                }}
              >
                <Button variant="secondary" width="130px" fontSize={theme.customFontsize(12, 14)}>
                  {t('sign_up')}
                </Button>
              </Link>
            </Box>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  )
}

export default Header
